<template>
  <div>
    <ModalConfirmation
      :showModal="showModal"
      :title="title"
    >
      <template v-slot:body>
        <div>
          <AddAccountModalCftWarning
            v-if="isActivate"
            :class="$style.warningCard"
          />
          <div v-if="isCreate">
            <InputLabel
              :class="$style.inputLabel"
              text="AWS Account Number"
            />
            <InputText
              v-model="accountNumber"
              :data-test-id="$testId('inputTextAccountNumber')"
              name="AWS Account Number"
              placeholder="000000000000"
              :required="true"
              rules="required|digits:12"
              showValidationMessage
            />
            <InputLabel
              :class="$style.inputLabel"
              text="AWS Account Alias"
            />
            <TooltipIcon
              ariaLabel="aria-label"
              hasIndicator
              icon="info"
              :isMarkdown="false"
              isDark
              orientation="top"
              text="Please create a nickname for this account."
            />
            <InputText
              v-model="alias"
              :data-test-id="$testId('inputTextAlias')"
              name="AWS Account Alias"
              showValidationMessage
            />
            <div :class="$style.regionLabel">
              <InputLabel
                :class="$style.inputLabel"
                text="AWS Region"
              />
              <TooltipIcon
                hasIndicator
                icon="info"
                :isMarkdown="false"
                isDark
                orientation="top"
                text="An instance of cloudtrail, s3 and other required infrastructure
                will be created in this region."
              />
            </div>
            <Select
              v-model="selectedRegion"
              :class="$style.regionDropdown"
              name="AWS Region"
              :options="$options.regionList"
              placeholder="Select"
              :required="true"
              rules="required"
            />
          </div>

          <div v-else-if="isActivate">
            <Link
              :class="$style.cftLink"
              :href="cloudInsightsAwsConfig.cftUrl"
              isExternal
              target="_blank"
              :text="bodyText"
            />
            <Checkbox
              v-model="haveRunCloud"
              :class="$style.checkbox"
              :data-test-id="$testId('haveRunCloud')"
              label="I have run Cloud Formations for this account"
            />
          </div>
        </div>
      </template>
      <template v-slot:footerAddition>
        <IconButton
          v-if="isActivate"
          :class="$style.backButton"
          :data-test-id="$testId('backButton')"
          hasNoBackground
          icon="arrowLeft"
          text="Back"
          type="informational"
          @click="reset"
        />
      </template>
      <template v-slot:footer>
        <AddAccountModalButtons
          v-if="isCreate"
          :data-test-id="$testId('modalButtonsCreate')"
          dataTestIdSuffix="create"
          :nextDisabled="nextBtnDisabled"
          :nextLoading="createLoading"
          @cancel="handleCancelClick"
          @next="handleNextClick"
        />
        <AddAccountModalButtons
          v-else-if="isActivate"
          :data-test-id="$testId('modalButtonsActivate')"
          dataTestIdSuffix="activate"
          :nextDisabled="!haveRunCloud"
          :nextLoading="activateLoading"
          nextText="Activate"
          @cancel="handleCancelClick"
          @next="handleActivateClick"
        />
      </template>
    </ModalConfirmation>
  </div>
</template>

<script>
import {
  Checkbox,
  IconButton,
  InputLabel,
  InputText,
  Link,
  ModalConfirmation,
  Select,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import { mapActions } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import AddAccountModalButtons from 'client/CloudInsights/components/AddAccountModalButtons';
import AddAccountModalCftWarning from 'client/CloudInsights/components/AddAccountModalCftWarning';
import CloudInsightsAccountsApi from 'client/api/v2/CloudInsightsAccountsApi';
import CloudInsightsConstants from 'client/CloudInsights/constants/CloudInsightsConstants';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';

const {
  routeConsts,
  routeNames,
} = CloudInsightsConstants;

const regionList = [
  { name: 'us-east-1', value: 'us-east-1' },
  { name: 'us-east-2', value: 'us-east-2' },
  { name: 'us-west-1', value: 'us-west-1' },
  { name: 'us-west-2', value: 'us-west-2' },
  { name: 'af-south-1', value: 'af-south-1' },
  { name: 'ap-east-1', value: 'ap-east-1' },
  { name: 'ap-northeast-1', value: 'ap-northeast-1' },
  { name: 'ap-northeast-2', value: 'ap-northeast-2' },
  { name: 'ap-northeast-3', value: 'ap-northeast-3' },
  { name: 'ap-south-1', value: 'ap-south-1' },
  { name: 'ap-southeast-1', value: 'ap-southeast-1' },
  { name: 'ap-southeast-2', value: 'ap-southeast-2' },
  { name: 'ap-southeast-3', value: 'ap-southeast-3' },
  { name: 'ca-central-1', value: 'ca-central-1' },
  { name: 'eu-central-1', value: 'eu-central-1' },
  { name: 'eu-north-1', value: 'eu-north-1' },
  { name: 'eu-south-1', value: 'eu-south-1' },
  { name: 'eu-west-1', value: 'eu-west-1' },
  { name: 'eu-west-2', value: 'eu-west-2' },
  { name: 'eu-west-3', value: 'eu-west-3' },
  { name: 'me-south-1', value: 'me-south-1' },
  { name: 'sa-east-1', value: 'sa-east-1' },
];

const bodyTextMap = {
  ACTIVATE: 'Run Cloud Formation Template In Your AWS Account',
  CREATE: 'All fields are mandatory.',
};

const cardMap = {
  ACTIVATE: 'ACTIVATE',
  CREATE: 'CREATE',
};

export default {
  name: 'AddAccountModal',

  components: {
    AddAccountModalButtons,
    AddAccountModalCftWarning,
    Checkbox,
    IconButton,
    InputLabel,
    InputText,
    Link,
    ModalConfirmation,
    Select,
    TooltipIcon,
  },

  mixins: [FeatureTrialsMixin],

  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      accountNumber: '',
      activateLoading: false,
      alias: '',
      cardView: cardMap.CREATE,
      cloudInsightsAwsConfig: {},
      createLoading: false,
      haveRunCloud: false,
      headerText: 'Enable Cloud Insights',
      selectedRegion: '',
    };
  },

  regionList,

  computed: {
    bodyText() {
      return bodyTextMap[this.cardView];
    },
    isActivate() {
      return this.cardView === cardMap.ACTIVATE;
    },
    isCreate() {
      return this.cardView === cardMap.CREATE;
    },
    nextBtnDisabled() {
      return this.accountNumber.length !== 12 || this.selectedRegion === '';
    },
    title() {
      let title = 'Enter Your AWS Account Number';
      if (this.isActivate) {
        title = 'Confirm Cloud Formation Run';
      }
      return title;
    },
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    handleAccountNumberClick() {
      if (this.cloudInsightsAwsConfig.cftUrl) {
        window.open(this.cloudInsightsAwsConfig.cftUrl, '_blank').focus();
      }
    },

    handleActivateClick() {
      this.activateLoading = true;
      const { objectId } = this.cloudInsightsAwsConfig.account;
      const payload = { isActivated: 'true', isValidated: 'false' };
      CloudInsightsAccountsApi.updateAccount(objectId, payload)
        .then(data => {
          this.cloudInsightsAwsConfig = data;
          this.$emit('activate');
          if (this.$hasFeatureFlag(this.$featureFlags.cloudInsightsTrial)) {
            this.refreshTrialData(trialFeatures.cloudInsights.featureCode);
          }

          if (this.$route.name !== routeNames[routeConsts.cloudInsightsAccounts]) {
            this.$router.push({ name: routeNames[routeConsts.cloudInsightsAccounts] });
          }
        })
        .catch(() => {
          this.triggerErrorNotification({
            title: 'Error',
            message: 'Could not update Account!',
          });
        })
        .finally(() => {
          this.activateLoading = false;
          this.reset();
        });
    },

    handleCancelClick() {
      this.reset();
      this.$emit('cancel');
    },

    handleNextClick() {
      this.createLoading = true;
      const payload = {
        awsAccountAlias: this.alias,
        awsAccountId: this.accountNumber,
        awsRegion: this.selectedRegion,
      };
      CloudInsightsAccountsApi.createAccount(payload)
        .then(data => {
          this.cloudInsightsAwsConfig = data;
          this.triggerSuccessNotification({
            title: 'Created',
            message: `${this.accountNumber} has been successfully created.`,
          });

          this.cardView = cardMap.ACTIVATE;
        })
        .catch(({ response }) => {
          let reason = '';
          let message = 'Could not create Account.';

          if (response) {
            const { status, statusText } = response;
            reason = status === 409
              ? `AWS Account ${this.accountNumber} already exists and is active.`
              : statusText;

            if (reason) {
              message = `${message} ${reason}`;
            }
          }

          this.triggerErrorNotification({
            title: 'Error',
            message,
          });
        })
        .finally(() => {
          this.createLoading = false;
        });
    },

    reset() {
      this.accountNumber = '';
      this.alias = '';
      this.cardView = cardMap.CREATE;
      this.cloudInsightsAwsConfig = {};
      this.haveRunCloud = false;
      this.selectedRegion = '';
    },
  },
};
</script>

<style scoped module>
@value (jcInformationalBase) from '~css/brand-colors.css';

.regionDropdown {
  width: 210px !important;
}

.regionLabel {
  align-items: flex-end;
  display: flex;
  gap: var(--jc-spacer-x-small);
}

.checkbox {
  margin: var(--jc-spacer-large) 0 var(--jc-spacer) 0;
}

.backButton {
  color: var(--jc-color-neutral-text-subdued);
}

.cftLink {
  display: inline-block;
  margin-top: var(--jc-spacer-large);
}

.inputLabel {
  font-size: var(--jcBody);
}
</style>
